export type LanguageCode = 'en' | 'hi' | 'as' | 'gu';

export interface LanguageOption {
  code: LanguageCode;
  /** English name of the language, shown for clarity */
  label: string;
  /** Native script label, shown as the primary option text */
  nativeLabel: string;
}

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी' },
  { code: 'as', label: 'Assamese', nativeLabel: 'অসমীয়া' },
  { code: 'gu', label: 'Gujarati', nativeLabel: 'ગુજરાતી' },
];

export const SUPPORTED_LANGUAGE_CODES: LanguageCode[] = SUPPORTED_LANGUAGES.map(
  (l) => l.code
);

/** Cookie name that persists the user's selected language across logout. */
export const LANGUAGE_STORAGE_KEY = 'appLanguage';

/** Cookie lifetime for the language preference (1 year). */
export const LANGUAGE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/**
 * When enabled, any translation key that is missing for the active (non-English)
 * language is translated on the fly through an external translation API and
 * cached locally. When disabled, missing keys simply fall back to English.
 *
 * Controlled by the `NX_LANGUAGE_API_ENABLED` env var ("true" to enable).
 */
export const languageApiEnabled =
  (process.env.NX_LANGUAGE_API_ENABLED ?? 'false') === 'true';

/**
 * Endpoint used for on-the-fly translation when `languageApiEnabled` is true.
 * Expected contract: POST { text, source, target } -> { translatedText }.
 * If empty, the API fallback is a no-op and English text is kept.
 */
export const TRANSLATION_API_URL = process.env.NX_TRANSLATION_API_URL ?? '';

export const isSupportedLanguage = (
  value: string | null | undefined
): value is LanguageCode =>
  !!value && SUPPORTED_LANGUAGE_CODES.includes(value as LanguageCode);
